import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import { SyntheticEvent, useEffect, useState } from "react";

import { Box, Button, useToast } from "@/storybook-components";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import {
  fetchIdeas,
  saveIdea,
  updateOneIdea
} from "@/redux/features/brainstormers/thunk";

import ContentDrawer from "../common/drawer";

import { fileUpload } from "@/models";
import CreateIdea from "./create/index";
import { setStatus } from "@/redux/features/questions/slice";
import { setCloseIdeaStatus } from "@/redux/features/brainstormers/slice";
import useUser from "@/hooks/useUser";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";
import { scrollIntoView } from "@/lib";

interface PostDrawerProps {
  onOpen: () => void;
  isOpen: boolean;
  onClose: () => void;
  type: string;
  isEditPost: boolean;
  editHandler?: (e: boolean) => void;
  currentPostData?: { [key: string]: string };
}

const OpenPostDrawer = ({
  onOpen,
  isOpen,
  onClose,
  type,
  isEditPost,
  editHandler,
  currentPostData
}: PostDrawerProps): JSX.Element => {
  const { translate } = useTranslator();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { query } = useRouter();
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { features } = useAppSelector((state) => state.features);
  const isNewCookieConsentEnabled = features?.newCookieConsentManager?.enabled;

  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);

  const { token } = fetchAuthToken({ user, anonymousUser });

  const { brainstormer, status } = useAppSelector(
    (state) => state?.brainstormers
  );
  const { allowUnverifiedParticipation } = brainstormer;

  const { errors } = useAppSelector((state) => state.questions);
  const { isRegisteredUserLoggedIn } = useUser();

  const routerPath = router.asPath;
  const { action } = router.query;
  const toast = useToast();

  useEffect(() => {
    if (action === "new_idea") onOpen();
  }, [onOpen, action]);

  const [ideaSubmitting, setIdeaSubmitting] = useState(false);

  const initialPostData = {
    name: "",
    description: "",
    mediaUrl: "",
    backgroundColourId: null
  };
  const [postData, setPostData] = useState(initialPostData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialQuestionError = {
    name: "",
    mediaUrl: "",
    terms: "",
    backgroundColourId: ""
  };
  const [questionError, setQuestionError] = useState(initialQuestionError);
  const [hasErrorInSubmission, setHasErrorInSubmission] = useState(false);

  const closeDrawer = () => {
    onClose();
    removeQueryParam();
  };

  const cancelPost = async () => {
    if (hasErrorInSubmission) await setHasErrorInSubmission(false);
    closeDrawer();
    if (isEditPost) {
      dispatch(setCloseIdeaStatus("closingIdea"));
      editHandler(false);
    }
    await setPostData(initialPostData);
    await setQuestionError(initialQuestionError);
  };

  const removeQueryParam = () => {
    const [updatedPath] = routerPath.split("?");
    router.replace(updatedPath, undefined, { shallow: true });
  };

  const createOrUpdateIdea = async () => {
    const { name, description, mediaUrl, backgroundColourId } = postData;

    // conver data uri to image file and than get the uploaded image url from BE
    let imageUrl = mediaUrl.includes("base64") ? "" : mediaUrl;
    if (!imageUrl && mediaUrl) {
      const file = await fetch(mediaUrl).then((res) => res.blob());

      const uploadedFile = await fileUpload(token, baseUrl, file);
      imageUrl = uploadedFile.url;
    }

    try {
      let response;
      if (isEditPost) {
        response = await dispatch(
          updateOneIdea({
            ideaId: currentPostData.id,
            name,
            description,
            mediaUrl: imageUrl,
            backgroundColourId
          })
        );
      } else {
        response = await dispatch(
          saveIdea({
            name,
            description,
            mediaUrl: imageUrl,
            backgroundColourId
          })
        );
      }
      if (!!response && !response.error) {
        const toastTitle = isEditPost
          ? translate("brainstormers.youHaveUpdatedYourContent")
          : translate("brainstormers.youHavePostedYourContent");

        toast({
          id: "idea-toast",
          description: toastTitle,
          status: "success",
          isClosable: true
        });
        setTimeout(() => {
          cancelPost();
        }, 100);
      }
      // eslint-disable-next-line no-unused-vars
    } catch (err) {
      toast({
        id: "idea-toast",
        title: translate("errors.sorrySomethingWentWrong"),
        status: "error",
        isClosable: true
      });
      setTimeout(() => {
        cancelPost();
      }, 100);
    }
  };

  const onSubmitHandler = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    let errorCount = 0;
    if (!postData.name) {
      await setQuestionError((prevQuestionError) => ({
        ...prevQuestionError,
        name: "blank"
      }));
      errorCount += 1;
    }
    if (!postData.mediaUrl && !postData.backgroundColourId) {
      setQuestionError((prevQuestionError) => ({
        ...prevQuestionError,
        backgroundColourId: "empty",
        mediaUrl: "empty"
      }));
      errorCount += 1;
    }

    if (allowUnverifiedParticipation && !isRegisteredUserLoggedIn) {
      if (errors?.email) errorCount += 1;

      if (errorCount <= 0) {
        setIdeaSubmitting(true);
        dispatch(setStatus("saving"));
      }
    }

    if (errorCount > 0) {
      setHasErrorInSubmission(true);
      setIsSubmitting(false);

      // Scroll to first error and focus on it
      setTimeout(() => {
        const errorElement = document.querySelector(".ehq-question-error");
        if (errorElement) {
          const focusableElement = errorElement.querySelector(
            "input, button, textarea, select"
          ) as HTMLElement;

          if (focusableElement) {
            focusableElement.focus();
          }

          scrollIntoView(".ehq-question-error");
        }
      }, 100);

      return false;
    }

    setHasErrorInSubmission(false);
    await createOrUpdateIdea();
    dispatch(setStatus("idle"));
    setIdeaSubmitting(false);

    // refresh and update the board with new post
    if (!isEditPost) {
      await dispatch(
        fetchIdeas({
          projectId: query.projectId as string,
          brainstormerId: query.brainstormerId as string,
          pageSize: 20,
          page: 1
        })
      );
    }
  };
  const FooterButton = ({
    variant,
    onClickHandler,
    text,
    isLoading,
    isDisabled = false
  }) => {
    const unverifiedParticipationCheck =
      isRegisteredUserLoggedIn || allowUnverifiedParticipation;
    return (
      <Button
        w={{ base: 160, lg: 120 }}
        size={{ base: "lg", xl: "md" }}
        maxW={160}
        px={4}
        py={2}
        variant={variant}
        onClick={onClickHandler}
        isLoading={isLoading}
        isDisabled={isDisabled || !unverifiedParticipationCheck}
      >
        {text}
      </Button>
    );
  };

  const title =
    type === "create"
      ? translate("brainstormers.createPost")
      : translate("brainstormers.editPost");

  const contentDrawerZIndex = isNewCookieConsentEnabled ? 1200 : 1000;
  return (
    <ContentDrawer
      isOpen={isOpen}
      onClose={cancelPost}
      onCloseComplete={null}
      header={title}
      zIndex={contentDrawerZIndex}
      trapFocus
      content={
        <CreateIdea
          postData={postData}
          setPostData={setPostData}
          questionError={questionError}
          setQuestionError={setQuestionError}
          hasErrorInSubmission={hasErrorInSubmission}
          ideaSubmitting={ideaSubmitting}
          isEditPost={isEditPost}
          currentPostData={currentPostData}
          onSubmit={onSubmitHandler}
        />
      }
      footer={
        <Box
          display="flex"
          alignItems="flex-start"
          alignSelf="stretch"
          justifyContent={{ base: "space-between", lg: "flex-end" }}
          gap={6}
          px={4}
          width="full"
        >
          <FooterButton
            variant="secondary"
            onClickHandler={cancelPost}
            text={translate("common.cancel")}
            isLoading={false}
          />
          <FooterButton
            variant="primary"
            onClickHandler={onSubmitHandler}
            text={
              isEditPost ? translate("common.update") : translate("common.post")
            }
            isLoading={
              isSubmitting || status === "ideaSaving" || ideaSubmitting
            }
            isDisabled={
              isSubmitting || status === "ideaSaving" || ideaSubmitting
            }
          />
        </Box>
      }
    />
  );
};

export default OpenPostDrawer;
